import { useEffect, useMemo, useState } from 'react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format, subMonths, startOfMonth } from 'date-fns';
import { toast } from 'sonner';
import { Truck, TrendingUp, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

const fmtEur = (n) => new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtKg = (n) => `${new Intl.NumberFormat('fi-FI', { maximumFractionDigits: 1 }).format(n || 0)} kg`;
const fmtDate = (iso) => {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const TABS = [
  { key: 'transport', label: 'Kuljetuskulut', icon: Truck },
  { key: 'profit', label: 'Asiakaskannattavuus', icon: TrendingUp },
];

export default function Reports() {
  // Oletusväli: kuluva kuukausi (1. päivä → tänään)
  const today = format(new Date(), 'yyyy-MM-dd');
  const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [df, setDf] = useState(monthStart);
  const [dt, setDt] = useState(today);
  const [tab, setTab] = useState('transport');
  const [transport, setTransport] = useState(null);
  const [profit, setProfit] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!df || !dt) return;
    setLoading(true);
    try {
      if (tab === 'transport') {
        const { data } = await api.get(`/reports/transport?from=${df}&to=${dt}`);
        setTransport(data);
      } else {
        const { data } = await api.get(`/reports/customer-profitability?from=${df}&to=${dt}`);
        setProfit(data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, df, dt]);

  // Pikavalinnat aikavälille
  const setLastMonth = () => {
    const d = subMonths(new Date(), 1);
    setDf(format(startOfMonth(d), 'yyyy-MM-dd'));
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    setDt(format(lastDay, 'yyyy-MM-dd'));
  };
  const setThisMonth = () => {
    setDf(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    setDt(today);
  };
  const setYtd = () => {
    setDf(`${new Date().getFullYear()}-01-01`);
    setDt(today);
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">Logistiikka</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">Raportit</h1>
        <p className="text-sm text-[#6A6D66] mt-1 max-w-2xl">
          Kuljetuskulut ja asiakaskannattavuus valitulta ajalta. <strong>JM</strong>: {fmtEur(15.10)} per pysäkki + {fmtEur(0.119)}/kg. <strong>Tampere-Lahti</strong>: {fmtEur(0.34)}/kg (sis. ALV 25,5%).
        </p>
      </div>

      {/* Aikavälin valinta */}
      <div className="bg-white border border-[#E5E6E3] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Alkaen</Label>
          <Input
            type="date"
            value={df}
            onChange={(e) => setDf(e.target.value)}
            className="h-9 w-40"
            data-testid="reports-from"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Päättyen</Label>
          <Input
            type="date"
            value={dt}
            onChange={(e) => setDt(e.target.value)}
            className="h-9 w-40"
            data-testid="reports-to"
          />
        </div>
        <div className="flex items-center gap-1.5 ml-2">
          <Button variant="outline" onClick={setThisMonth} className="h-8 text-xs" data-testid="reports-quick-this-month">Tämä kk</Button>
          <Button variant="outline" onClick={setLastMonth} className="h-8 text-xs" data-testid="reports-quick-last-month">Edellinen kk</Button>
          <Button variant="outline" onClick={setYtd} className="h-8 text-xs" data-testid="reports-quick-ytd">Vuoden alusta</Button>
        </div>
        {loading && (
          <div className="ml-auto flex items-center gap-2 text-xs text-[#6A6D66]">
            <Loader2 size={14} className="animate-spin" />
            Lataa…
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E5E6E3]">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`reports-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-[#2A4B30] text-[#2A4B30]'
                  : 'border-transparent text-[#6A6D66] hover:text-[#1C1C1A]'
              }`}
            >
              <Icon size={15} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'transport' && <TransportView data={transport} />}
      {tab === 'profit' && <ProfitView data={profit} />}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-[#E5E6E3] rounded-xl p-4">
      <p className="text-[11px] uppercase tracking-wider text-[#6A6D66] font-semibold">{label}</p>
      <p className="font-heading text-3xl text-[#1C1C1A] mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-[#6A6D66] mt-1">{sub}</p>}
    </div>
  );
}

function TransportView({ data }) {
  const [expandedTL, setExpandedTL] = useState(false);
  if (!data) return null;
  const { jm, tampere_lahti: tl } = data;

  return (
    <div className="space-y-6">
      {/* JM section */}
      <section className="space-y-3">
        <h2 className="font-heading text-xl text-[#1C1C1A]">JM (Järvimäki) — Auto 1, Auto 2, …</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label="Yhteensä kuluja" value={fmtEur(jm.total_cost)} data-testid="jm-total-cost"
            sub={`${jm.total_stops} pysäkkiä × ${fmtEur(jm.stop_fee)} + ${fmtKg(jm.total_kg)} × ${fmtEur(jm.per_kg)}/kg`}
          />
          <StatCard label="Kuljetetut kg" value={fmtKg(jm.total_kg)} />
          <StatCard label="Pysäkkejä" value={jm.total_stops} sub={`${jm.by_route.length} reittiä`} />
        </div>

        {jm.by_route.length > 0 && (
          <div className="bg-white border border-[#E5E6E3] rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-[#F9F9F6] border-b border-[#E5E6E3]">
              <p className="text-xs uppercase tracking-wider text-[#6A6D66] font-semibold">Reitit</p>
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[#6A6D66] border-b border-[#E5E6E3]">
                <tr>
                  <th className="px-4 py-2 text-xs font-semibold">Reitti</th>
                  <th className="px-4 py-2 text-xs font-semibold text-right">Pysäkit</th>
                  <th className="px-4 py-2 text-xs font-semibold text-right">Kg</th>
                  <th className="px-4 py-2 text-xs font-semibold text-right">Kuljetuskulu</th>
                </tr>
              </thead>
              <tbody>
                {jm.by_route.map((r) => (
                  <tr key={r.route} className="border-b border-[#E5E6E3] last:border-b-0" data-testid={`jm-route-${r.route}`}>
                    <td className="px-4 py-2 font-medium">{r.route}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.stops}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtKg(r.kg)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtEur(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {jm.by_day.length > 0 && <DayBreakdown title="Päiväkohtaisesti" days={jm.by_day} showCost />}
      </section>

      {/* Tampere-Lahti section */}
      <section className="space-y-3">
        <h2 className="font-heading text-xl text-[#1C1C1A]">Tampere & Lahti -jakelu</h2>
        <p className="text-xs text-[#6A6D66] -mt-2">Hinnoittelu: {fmtEur(tl.per_kg || 0.34)}/kg (sis. ALV 25,5%)</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatCard label="Yhteensä kuluja" value={fmtEur(tl.total_cost || 0)}
            sub={`${fmtKg(tl.total_kg)} × ${fmtEur(tl.per_kg || 0.34)}/kg`}
          />
          <StatCard label="Kuljetetut kg" value={fmtKg(tl.total_kg)} />
          <StatCard label="Pysäkkejä" value={tl.total_stops} sub={`${tl.by_day.length} toimituspäivää`} />
        </div>

        {tl.by_day.length > 0 && (
          <div className="bg-white border border-[#E5E6E3] rounded-xl overflow-hidden">
            <button
              onClick={() => setExpandedTL((v) => !v)}
              data-testid="tl-expand"
              className="w-full px-4 py-2.5 bg-[#F9F9F6] border-b border-[#E5E6E3] flex items-center gap-2 hover:bg-[#F0F1EE] transition-colors"
            >
              {expandedTL ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="text-xs uppercase tracking-wider text-[#6A6D66] font-semibold">
                Päiväkohtaisesti — {expandedTL ? 'piilota' : 'näytä'}
              </span>
            </button>
            {expandedTL && (
              <div className="divide-y divide-[#E5E6E3]">
                {tl.by_day.map((d) => (
                  <div key={d.date} className="px-4 py-3" data-testid={`tl-day-${d.date}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-medium capitalize text-sm">{fmtDate(d.date)}</span>
                      <div className="flex items-center gap-3 text-sm tabular-nums text-[#6A6D66]">
                        <span>{d.stops} pysäkkiä</span>
                        <span className="text-[#1C1C1A] font-medium">{fmtKg(d.kg)}</span>
                        <span className="text-[#2A4B30] font-medium">{fmtEur(d.cost || 0)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {d.customers.map((c, i) => (
                        <span
                          key={`${c.customer_id}-${i}`}
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#F4F6F3] border border-[#E5E6E3] rounded-full text-xs"
                        >
                          <span>{c.customer_name}</span>
                          <span className="text-[#6A6D66] tabular-nums">{fmtKg(c.kg)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function DayBreakdown({ title, days, showCost }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-[#E5E6E3] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="jm-day-expand"
        className="w-full px-4 py-2.5 bg-[#F9F9F6] border-b border-[#E5E6E3] flex items-center gap-2 hover:bg-[#F0F1EE] transition-colors"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="text-xs uppercase tracking-wider text-[#6A6D66] font-semibold">
          {title} — {expanded ? 'piilota' : 'näytä'}
        </span>
      </button>
      {expanded && (
        <table className="w-full text-sm">
          <thead className="text-left text-[#6A6D66] border-b border-[#E5E6E3]">
            <tr>
              <th className="px-4 py-2 text-xs font-semibold">Päivä</th>
              <th className="px-4 py-2 text-xs font-semibold text-right">Pysäkit</th>
              <th className="px-4 py-2 text-xs font-semibold text-right">Kg</th>
              {showCost && <th className="px-4 py-2 text-xs font-semibold text-right">Kuljetuskulu</th>}
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.date} className="border-b border-[#E5E6E3] last:border-b-0" data-testid={`jm-day-${d.date}`}>
                <td className="px-4 py-2 capitalize">{fmtDate(d.date)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{d.stops}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtKg(d.kg)}</td>
                {showCost && <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtEur(d.cost)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ProfitView({ data }) {
  const [sortBy, setSortBy] = useState('net');  // 'net' | 'margin_pct' | 'revenue_gross' | 'kg'
  const rows = useMemo(() => {
    if (!data?.rows) return [];
    const sorted = [...data.rows].sort((a, b) => {
      const diff = (b[sortBy] || 0) - (a[sortBy] || 0);
      return diff !== 0 ? diff : (a.customer_name || '').localeCompare(b.customer_name || '', 'fi');
    });
    return sorted;
  }, [data, sortBy]);

  if (!data) return null;
  if (!rows.length) {
    return (
      <div className="bg-white border border-[#E5E6E3] rounded-xl p-10 text-center text-sm text-[#6A6D66]">
        Ei tilauksia tai laskuja tällä aikavälillä.
      </div>
    );
  }

  const totalRev = rows.reduce((a, r) => a + (r.revenue_gross || 0), 0);
  const totalCost = rows.reduce((a, r) => a + (r.transport_cost || 0), 0);
  const totalKg = rows.reduce((a, r) => a + (r.kg || 0), 0);
  const totalNet = totalRev - totalCost;

  const sortBtn = (key, label) => (
    <button
      onClick={() => setSortBy(key)}
      data-testid={`profit-sort-${key}`}
      className={`px-2 py-1 rounded text-[11px] uppercase tracking-wider font-semibold ${
        sortBy === key ? 'bg-[#2A4B30] text-white' : 'text-[#6A6D66] hover:bg-[#E5E6E3]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Liikevaihto" value={fmtEur(totalRev)} sub={`${rows.length} asiakasta`} />
        <StatCard label="Kg toimitettu" value={fmtKg(totalKg)}
          sub={totalKg > 0 && totalRev > 0 ? `keskihinta ${fmtEur(totalRev / totalKg)}/kg` : null} />
        <StatCard label="Kuljetuskulut" value={fmtEur(totalCost)} sub="JM + Tampere-Lahti" />
        <StatCard label="Nettokate" value={fmtEur(totalNet)}
          sub={totalRev > 0 ? `${((totalNet / totalRev) * 100).toFixed(1)} %` : '—'} />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-[#6A6D66] mr-1">Järjestä:</span>
        {sortBtn('net', 'Nettokate')}
        {sortBtn('revenue_gross', 'Liikevaihto')}
        {sortBtn('margin_pct', 'Kate-%')}
        {sortBtn('avg_price_per_kg', 'Keskihinta')}
        {sortBtn('kg', 'Kg')}
      </div>

      <div className="bg-white border border-[#E5E6E3] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[#6A6D66] bg-[#F9F9F6] border-b border-[#E5E6E3]">
              <tr>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider">Asiakas</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider">Reitti</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right">Päivät</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right">Kg</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right">Liikevaihto</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right" title="Keskihinta = liikevaihto / kg">€/kg</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right">Kuljetus</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right">Kate</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.customer_id} className="border-b border-[#E5E6E3] last:border-b-0" data-testid={`profit-row-${r.customer_id}`}>
                  <td className="px-4 py-2 font-medium text-[#1C1C1A]">{r.customer_name || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      r.route_category === 'JM' ? 'bg-[#FFF3E0] text-[#8B4513]' :
                      r.route_category === 'Tampere-Lahti' ? 'bg-[#E3F2FD] text-[#1565C0]' :
                      'bg-[#F4F6F3] text-[#6A6D66]'
                    }`}>
                      {r.route || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.delivery_days}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtKg(r.kg)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtEur(r.revenue_gross)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-xs text-[#6A6D66]">
                    {r.avg_price_per_kg > 0 ? `${fmtEur(r.avg_price_per_kg)}/kg` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#C05A3C]">
                    {r.transport_cost > 0 ? `−${fmtEur(r.transport_cost)}` : fmtEur(0)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtEur(r.net)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-xs text-[#6A6D66]">
                    {r.revenue_gross > 0 ? `${r.margin_pct.toFixed(1)} %` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
